import { Badge, Button, Group, Paper, Stack, Text, Transition } from "@mantine/core";
import { type FC, useEffect, useState } from "react";
import type { EvaluationDetailsBody } from "../api/evaluations/types";
import classes from "./EvaluationPage.module.css";

export interface EvaluationCalloutProps {
    activeBehavior?: EvaluationDetailsBody;
    onClickOk: () => void;
    pagination?: React.ReactNode;
    scrollOffset: number;
}

const EvaluationCalloutBadge: FC<{ behaviorPresent: 0 | 1 }> = ({ behaviorPresent }) => (
    <Badge
        variant="outline"
        color={behaviorPresent ? "var(--mantine-color-success-5)" : "var(--mantine-color-primary-3)"}
        bdrs="5px"
    >
        {behaviorPresent ? "Great job" : "Growth area"}
    </Badge>
);

export const EvaluationCallout: FC<EvaluationCalloutProps> = ({
    activeBehavior,
    onClickOk,
    pagination,
    scrollOffset,
}) => {
    const [opened, setOpened] = useState(false);

    useEffect(() => {
        setOpened(true);
    }, []);

    return (
        <Transition
            mounted={opened}
            transition="fade"
            duration={400}
            timingFunction="ease"
            enterDelay={200}
            exitDelay={300}
        >
            {(transitionStyle) => (
                <Paper
                    shadow="xl"
                    w="100%"
                    bg="var(--mantine-color-dark-9)"
                    bdrs="0.75rem"
                    style={{
                        ...transitionStyle,
                        position: "relative",
                        top: scrollOffset,
                    }}
                >
                    <Group py="0.75rem" px="1.25rem" className={classes.calloutHeader} gap="0.5rem">
                        <Text size="sm" fw="700" className={classes.calloutHeaderTitle}>
                            {activeBehavior?.behavior ?? "Challenge Evaluation"}
                        </Text>
                        {activeBehavior?.behaviorPresent !== undefined && (
                            <EvaluationCalloutBadge behaviorPresent={activeBehavior?.behaviorPresent} />
                        )}
                        {pagination && (
                            <Group justify="flex-end" flex="1">
                                {pagination}
                            </Group>
                        )}
                    </Group>
                    <Stack p="1.25rem" gap="1.75rem">
                        {activeBehavior?.content ? (
                            <Text size="sm" fw="500" lh="150%">
                                {activeBehavior?.content}
                            </Text>
                        ) : (
                            <Text size="sm" fw="500" lh="150%">
                                There are no behaviors to review, please press the &ldquo;OK, got it&rdquo; button to
                                return to the available challenges.
                            </Text>
                        )}
                        <Group justify="flex-end">
                            <Button
                                size="xs"
                                color="var(--mantine-color-success-5)"
                                onClick={() => {
                                    setOpened(false);
                                    onClickOk();
                                }}
                            >
                                OK, got it
                            </Button>
                        </Group>
                    </Stack>
                </Paper>
            )}
        </Transition>
    );
};

export default EvaluationCallout;
